import { Fragment, useCallback, useState, useEffect } from 'react';
import { object, string } from 'yup';
import { useFormik } from 'formik';
import { Alert, Box, Button, Tooltip, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useDispatch, cancelOrder, customPost } from '../../../state';
import { useNotify, useGetUsersData } from '../../../hooks';
import { CancelIcon } from '../../../assets';
import {
  authPost,
  generateUrl,
  orderStatuses,
  paths,
  Navigation,
} from '../../../lib';
import { FormModal, TextInput } from '../..';

interface CancelOrderProps {
  orderId: string | number;
  orderStatus?: string;
  hasPickedHistory?: boolean;
  modalOpened: boolean;
  successCallback?: () => void;
  onCancel?: () => void;
}

interface State {
  reason: string;
}

interface CancelOrderClaim {
  id: number;
  sku?: string;
  locationName?: string;
  moveablePalletName?: string;
  receiptID?: number;
  receiptReferenceNumber?: string;
  quantity: number;
  liveQuantity: number;
  physicalItems: Array<{
    id: number;
    physicalItemID: number;
    quantity: number;
    lotNumber?: string;
    serialNumber?: string;
    expirationDate?: string;
  }>;
}

interface CancelOrderReview {
  orderID: number;
  orderStatus: string;
  canCancel: boolean;
  blockers: string[];
  billingWarnings?: string[];
  ownershipClaims: CancelOrderClaim[];
  palletClaims: CancelOrderClaim[];
  feeImpact: Array<{
    description: string;
    amount: number;
    alreadyCharged: boolean;
  }>;
}

const initialState: State = {
  reason: '',
};

const prePickStatuses: string[] = [
  orderStatuses.OPEN,
  orderStatuses.PENDING,
  orderStatuses.ALLOCATED,
  orderStatuses.OVER_ALLOCATED,
  orderStatuses.PARTIALLY_ALLOCATED,
];

export function CancelOrder({
  orderId,
  orderStatus,
  hasPickedHistory,
  modalOpened,
  successCallback,
  onCancel,
}: CancelOrderProps) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const notify = useNotify();
  const { isAdmin, isCustomerService, isTruckingSalesPerson, isCustomer } =
    useGetUsersData();
  const [showManagerLogin, setShowManagerLogin] = useState(false);
  const hasEditPermission =
    isAdmin || isCustomerService || isTruckingSalesPerson || isCustomer;
  const requiresReview = hasPickedHistory || !orderStatus || !prePickStatuses.includes(orderStatus);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [adminToken, setAdminToken] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [review, setReview] = useState<CancelOrderReview>();
  const [reviewError, setReviewError] = useState<string>();

  const loadReview = useCallback(
    async (token?: string) => {
      setReviewing(true);
      setReviewError(undefined);
      const result = token
        ? await dispatch(
            customPost<CancelOrderReview>({
              url: `orders/${orderId}/cancel/review`,
              authToken: token,
              body: {},
            }),
          )
        : await authPost<CancelOrderReview>(
            `orders/${orderId}/cancel/review`,
            {},
          );
      setReviewing(false);
      if (result.error || !result.data) {
        setReview(undefined);
        setReviewError(
          result.error?.message ||
            result.error?.title ||
            'Unable to review cancellation impact',
        );
        return undefined;
      }
      setReview(result.data);
      return result.data;
    },
    [dispatch, orderId],
  );

  const handleShowModal = useCallback(() => {
    if (hasEditPermission) {
      setShowConfirmation(true);
      if (requiresReview) void loadReview();
    } else {
      setShowManagerLogin(true);
    }
  }, [hasEditPermission, loadReview, requiresReview]);

  useEffect(() => {
    if (modalOpened) {
      handleShowModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteOrder = useCallback(
    async (values: State) => {
      setSubmitting(true);
      if (requiresReview) {
        const latestReview = await loadReview(adminToken);
        if (!latestReview || !latestReview.canCancel) {
          setSubmitting(false);
          return;
        }
        if (JSON.stringify(latestReview) !== JSON.stringify(review)) {
          setSubmitting(false);
          notify('Cancellation impact changed. Please review it again.', 'error');
          return;
        }
      }
      const payload = { reason: values.reason };
      const { error } = await dispatch(
        cancelOrder({ orderId, token: adminToken, payload }),
      );
      setSubmitting(false);
      if (error) {
        return notify(
          error.message || error.title || 'Something went wrong',
          'error',
        );
      }
      notify('Order successfully cancelled');
      successCallback
        ? successCallback()
        : Navigation.redirect(generateUrl(paths.SCAN_ORDER));
    },
    [adminToken, dispatch, successCallback, notify, orderId, loadReview, review, requiresReview],
  );

  const onAdminLogin = useCallback((token: string) => {
    setShowManagerLogin(false);
    setShowConfirmation(true);
    setAdminToken(token);
    if (requiresReview) void loadReview(token);
  }, [loadReview, requiresReview]);

  const handleCancel = useCallback(() => {
    setShowConfirmation(false);
    setShowManagerLogin(false);
    setReview(undefined);
    setReviewError(undefined);
    onCancel && onCancel();
  }, [onCancel]);

  const formik = useFormik<State>({
    initialValues: initialState,
    enableReinitialize: false,
    onSubmit: handleDeleteOrder,
    validationSchema: schema,
  });

  return (
    <Fragment>
      {!modalOpened && (
        <Tooltip title='Cancel order' placement='bottom'>
          <Button
            color='secondary'
            className={classes.actionButton}
            size='small'
            onClick={handleShowModal}
          >
            <CancelIcon className={classes.icon} />
          </Button>
        </Tooltip>
      )}
      <FormModal
        open={showConfirmation || showManagerLogin}
        handleClose={handleCancel}
        title={
          showManagerLogin
            ? 'Manager login required to complete edits'
            : 'Are you sure you want to cancel this order?'
        }
        callback={formik.handleSubmit}
        btnText='Confirm cancellation'
        paperProps={{ style: { width: 640 } }}
        showManagerLogin={showManagerLogin}
        onAdminLogin={onAdminLogin}
        submitting={submitting || reviewing}
        disableSubmit={requiresReview && (!review?.canCancel || reviewing || !!reviewError)}
        typeSubmit
      >
        {reviewing && <Typography>Loading inventory and fee impact…</Typography>}
        {reviewError && <Alert severity='error'>{reviewError}</Alert>}
        {review?.blockers.map((blocker, index) => (
          <Alert severity='error' key={`${index}-${blocker}`}>
            {blocker}
          </Alert>
        ))}
        {review?.billingWarnings?.map((warning, index) => (
          <Alert severity='warning' key={`${index}-${warning}`}>
            {warning}
          </Alert>
        ))}
        {review && (
          <Box sx={{ mb: 2 }}>
            <Alert severity='info' sx={{ mb: 1 }}>
              These items will no longer be reserved for this order. They will
              stay in staging until warehouse staff move them. Keep them separate
              from items shipping on other orders.
            </Alert>
            <Typography fontWeight={600}>Items returning to available stock</Typography>
            {review.ownershipClaims.length === 0 && (
              <Typography>None</Typography>
            )}
            {review.ownershipClaims.map((claim) => (
              <Box key={claim.id}>
                <Typography>
                  {claim.sku || 'Item'}: {claim.quantity} units at{' '}
                  {claim.locationName || 'unknown location'} (receipt{' '}
                  {claim.receiptReferenceNumber || claim.receiptID || 'not available'})
                </Typography>
                {claim.physicalItems.map((item) => (
                  <Typography variant='body2' key={item.id} sx={{ ml: 2 }}>
                    {item.quantity} units
                    {item.lotNumber ? ` · lot ${item.lotNumber}` : ''}
                    {item.serialNumber ? ` · serial ${item.serialNumber}` : ''}
                    {item.expirationDate ? ` · expires ${item.expirationDate}` : ''}
                  </Typography>
                ))}
              </Box>
            ))}
            <Typography fontWeight={600} sx={{ mt: 1 }}>
              Where to find the staged items
            </Typography>
            <Typography variant='body2'>
              Check these pallets when separating the items. This is a location
              list, not an additional quantity to return.
            </Typography>
            {review.palletClaims.length === 0 && <Typography>None</Typography>}
            {review.palletClaims.map((claim) => (
              <Box key={claim.id}>
                <Typography>
                  {claim.sku || 'Item'}: {claim.liveQuantity} units on{' '}
                  {claim.moveablePalletName || 'a staged pallet'} at{' '}
                  {claim.locationName || 'unknown location'} (receipt{' '}
                  {claim.receiptReferenceNumber || claim.receiptID || 'not available'})
                </Typography>
                {/* Pallet labels identify the location; their original quantities
                    can include items released by an earlier revision. */}
                {claim.physicalItems.filter((item) =>
                  item.lotNumber || item.serialNumber || item.expirationDate,
                ).map((item) => (
                  <Typography variant='body2' key={item.id} sx={{ ml: 2 }}>
                    {[
                      item.lotNumber && `Lot ${item.lotNumber}`,
                      item.serialNumber && `Serial ${item.serialNumber}`,
                      item.expirationDate && `Expires ${item.expirationDate}`,
                    ].filter(Boolean).join(' · ')}
                  </Typography>
                ))}
              </Box>
            ))}
            <Typography fontWeight={600} sx={{ mt: 1 }}>
              Expected pick, return, and cancellation fees
            </Typography>
            {review.feeImpact.length === 0 && <Typography>No new fee</Typography>}
            {review.feeImpact.map((fee, index) => (
              <Typography key={`${index}-${fee.description}`}>
                {fee.description}: ${fee.amount.toFixed(2)}
                {fee.alreadyCharged ? ' (already charged)' : ''}
              </Typography>
            ))}
            <Typography variant='body2' sx={{ mt: 1 }}>
              These amounts include both retained and newly calculated charges;
              other configured service or shipping fees are not shown here. The
              final charge is recalculated when cancellation is saved. If inventory
              or fees change, review the updated impact before retrying.
            </Typography>
          </Box>
        )}
        <TextInput
          name='reason'
          formikProps={formik}
          label='Reason'
          multiline
          rows={3}
          required
        />
      </FormModal>
    </Fragment>
  );
}

const useStyles = makeStyles({ name: { CancelOrder } })((theme) => ({
  actionButton: {
    width: 32,
    height: 32,
    minWidth: 'unset',
    backgroundColor: '#F5F6FE',
  },
  icon: {
    fontSize: 14,
  },
}));

const schema = object().shape({
  reason: string().required('Required'),
});
